/**
 * Site-wide FAQ — long-tail SEO content + FAQPage schema.
 *
 * Strategy: each Q&A is a self-contained answer optimised for one
 * query intent. Aim for ~40-80 words per answer (Google's sweet
 * spot for featured snippets). Group by theme so the in-page TOC
 * is useful too.
 *
 * `FAQ_GROUPS` (default IT) preserved for backwards compatibility.
 * `FAQ_GROUPS_BY_LANG` exposes both locales; `getFaqGroups(lang)`
 * is the recommended accessor (graceful fallback to IT).
 */

const FAQ_IT = [
  {
    id: "valutazione",
    title: "Valutazione e prezzi",
    items: [
      {
        q: "Cosa significa OMI?",
        a: "OMI è l'Osservatorio del Mercato Immobiliare, gestito dall'Agenzia delle Entrate. Pubblica due volte l'anno le quotazioni medie €/m² per zona, tipologia (residenziale, commerciale, terreni) e stato di conservazione. Sono dati ufficiali e gratuiti, usati come riferimento da banche, periti e fisco.",
      },
      {
        q: "Come si calcola il valore al m² di una casa?",
        a: "Dividi il prezzo richiesto per la superficie commerciale in metri quadri. La superficie commerciale considera il 100% degli ambienti coperti, il 50-60% dei balconi e terrazzi, il 10-25% del giardino. Per confronto con i dati OMI usa sempre la superficie commerciale, non quella netta interna.",
      },
      {
        q: "Quando un immobile è davvero un affare?",
        a: "Quando il prezzo richiesto è almeno il 15-25% sotto la media OMI della zona E l'immobile non ha vizi nascosti (abusi, vincoli, problemi catastali, contestazioni). Un prezzo basso senza un perché chiaro è quasi sempre una trappola: chiediti perché il venditore svende.",
      },
      {
        q: "Cosa fa il punteggio Deal Score?",
        a: "Il Deal Score di Deal Rating va da 0 a 10 e combina: confronto col valore OMI di zona (±4 punti), qualità intrinseca dell'immobile e bonus contestuali (vista, ulivi, classe energetica), penalità per vincoli e rischi. 7-10 è un affare, 5-7 prezzo equo, sotto 5 è sopravvalutato.",
      },
    ],
  },
  {
    id: "fiscalita",
    title: "Tasse e costi",
    items: [
      {
        q: "Quanto si paga di IMU su una seconda casa in Puglia?",
        a: "L'IMU su seconda casa è applicata sulla rendita catastale rivalutata × moltiplicatore (160 per residenze categoria A). L'aliquota standard è del 8,6‰ ma i Comuni possono alzarla fino al 10,6‰. In Salento le aliquote variano da 8,6‰ (piccoli centri) a 10,6‰ (Lecce, Ostuni, Otranto).",
      },
      {
        q: "Quanto costa comprare casa includendo le tasse?",
        a: "Su prima casa da privato: imposta di registro 2% (sul valore catastale), imposta ipotecaria + catastale 50€ + 50€, notaio 1.500-3.000€, agenzia immobiliare 3% + IVA. Su seconda casa: imposta di registro 9% sul valore catastale. Calcola in totale +12-15% del prezzo per seconda casa.",
      },
      {
        q: "Conviene comprare con l'agevolazione prima casa?",
        a: "Sì se sposti la residenza nel Comune entro 18 mesi e non possiedi altre case in Italia. Risparmi ~5-7% sul prezzo. Non conviene se vuoi affittare l'immobile nei primi 5 anni (perdi l'agevolazione) o se hai già un altro immobile come prima casa.",
      },
    ],
  },
  {
    id: "vincoli",
    title: "Vincoli e burocrazia",
    items: [
      {
        q: "Cosa è il vincolo paesaggistico?",
        a: "Il vincolo paesaggistico (D.lgs 42/2004) impone l'autorizzazione paesaggistica per qualsiasi modifica esterna dell'immobile (infissi, intonaci, ampliamenti). In Salento è molto esteso: copre fascia costiera 300m, centri storici, ulivi monumentali, zone SIC/ZPS. Verificare sempre dal PUG comunale prima di acquistare.",
      },
      {
        q: "Si può costruire entro 300 metri dal mare in Puglia?",
        a: "No, salvo edifici preesistenti. Il vincolo Galasso (L. 431/85) blocca nuove costruzioni nella fascia 300m dalla battigia. Sono possibili solo manutenzione ordinaria e straordinaria su edifici già autorizzati. Nessun ampliamento volumetrico, nessuna sopraelevazione.",
      },
      {
        q: "Si possono trasformare i trulli in struttura ricettiva?",
        a: "Sì, ma con vincoli stretti. Il cono originale del trullo è bene di interesse storico-architettonico: non può essere alterato. Si possono aggiungere ambienti accessori (cucina, bagni, lamia di raccordo) solo se preesistenti o se l'ampliamento è coerente col Piano Paesaggistico Regionale (PPTR).",
      },
      {
        q: "Cos'è la conformità urbanistica e catastale?",
        a: "Conformità urbanistica: lo stato dei luoghi corrisponde al progetto autorizzato dal Comune. Conformità catastale: la planimetria catastale corrisponde alla realtà. Sono diverse: serve il rilievo da un tecnico abilitato. Difformità = abuso edilizio, va sanato prima del rogito (o c'è nullità del contratto).",
      },
    ],
  },
  {
    id: "tipologie",
    title: "Tipologie di immobili",
    items: [
      {
        q: "Cos'è una pajara salentina?",
        a: "La pajara è una costruzione rurale a secco tipica del Salento meridionale, simile al trullo ma di dimensioni più ridotte e con tecnica costruttiva diversa (pietre senza malta, copertura a falsa cupola). Era usata da pastori e contadini come riparo temporaneo. Oggi è apprezzata come bene paesaggistico.",
      },
      {
        q: "Qual è la differenza tra trullo e lamia?",
        a: "Il trullo ha pianta circolare e copertura conica a pseudo-cupola, è tipico della Valle d'Itria (Alberobello, Cisternino, Locorotondo, Martina, Ostuni). La lamia ha pianta rettangolare e copertura piana o a botte. Entrambi sono in pietra a secco e originariamente strutture rurali.",
      },
      {
        q: "Cosa significa villa fronte mare prima fila?",
        a: 'Significa che l\'immobile non ha altre costruzioni tra sé e il mare, con vista diretta sulla battigia. È il top di gamma del mercato turistico ma quasi sempre vincolato (entro fascia 300m, vincolo paesaggistico). Diffidare di annunci che dichiarano "vista mare" senza specificare la distanza reale.',
      },
    ],
  },
  {
    id: "investimento",
    title: "Investimento e rendita",
    items: [
      {
        q: "Quanto rende un affitto turistico nel Salento?",
        a: "Dipende dalla zona. Ostuni, Otranto e Gallipoli generano 6-10% yield lordo annuo. Lecce centro storico 5-8%. Brindisi e Taranto città 4-6%. Considera 15-25% di costi di gestione, manutenzione e periodi morti per arrivare allo yield netto reale.",
      },
      {
        q: "È meglio affitto breve turistico o lungo residenziale?",
        a: "Affitto breve: yield più alto (6-10%) ma stagionalità marcata, gestione intensa, normative locali in evoluzione. Affitto lungo: yield più basso (3-5%) ma cashflow stabile, meno gestione. In zone turistiche pure (Gallipoli, Otranto) breve vince; in città universitarie (Lecce) lungo è più equilibrato.",
      },
      {
        q: "Conviene comprare casa in Salento nel 2026?",
        a: 'Sì, se compri sotto il valore OMI, eviti vincoli pesanti non gestibili, e hai un orizzonte di almeno 5-7 anni. Il mercato è stato fortemente speculativo dal 2018, ma alcune aree (entroterra leccese, Brindisi, Taranto) sono ancora sotto-prezzate. Evita acquisti emotivi nei mercati "trofeo" (Ostuni centro, Otranto fronte mare).',
      },
    ],
  },
  {
    id: "rischi",
    title: "Rischi e tutele",
    items: [
      {
        q: "Come capire se un annuncio immobiliare è sopravvalutato?",
        a: "Confronta il prezzo €/m² con la media OMI della zona. Se è oltre +20% senza ragione chiara (vista mare diretta, restauro completo recente, ulivi monumentali certificati), è quasi certo sopravvalutato. Verifica anche il tempo sul mercato: annunci da più di 6 mesi senza variazioni sono spesso fuori prezzo.",
      },
      {
        q: "Cosa è la Xylella e come influisce sull'acquisto di terreni?",
        a: 'La Xylella fastidiosa è un batterio che ha devastato gli uliveti del Salento dal 2013. Prima di acquistare un terreno con ulivi: richiedere certificazione fitosanitaria recente (max 6 mesi), verificare che gli ulivi non siano monumentali (in caso lo siano e siano malati, l\'estirpazione è regolamentata).',
      },
      {
        q: "Posso comprare casa all'asta nel Salento?",
        a: "Sì ma con cautela. Le aste offrono ribassi del 30-50% sul mercato, ma spesso gli immobili hanno problematiche (occupanti, vincoli, lavori da fare). Verificare sempre la perizia, il regolamento dell'asta, e fare un sopralluogo pre-asta. Considerare un investimento speculativo, non residenziale.",
      },
    ],
  },
];

const FAQ_EN = [
  {
    id: "valutazione",
    title: "Valuation and pricing",
    items: [
      {
        q: "What does OMI mean?",
        a: "OMI is the Italian Revenue Agency's Real Estate Market Observatory. Twice a year it publishes average €/m² values by zone, property type (residential, commercial, land) and condition. The data is official and free, used as a reference by banks, surveyors and tax authorities across Italy.",
      },
      {
        q: "How is the €/m² of a house calculated?",
        a: "Divide the asking price by the commercial surface in square metres. Commercial surface counts 100% of covered rooms, 50-60% of balconies and terraces, 10-25% of garden. When comparing with OMI data always use commercial surface, not net internal area.",
      },
      {
        q: "When is a property really a bargain?",
        a: "When the asking price is at least 15-25% below the OMI zone average AND there are no hidden defects (unauthorised works, planning constraints, cadastral issues, disputes). A low price without a clear reason is almost always a trap — ask yourself why the seller is undercutting the market.",
      },
      {
        q: "What is the Deal Score?",
        a: "The Deal Rating Deal Score ranges 0-10 and combines: comparison with the OMI zone benchmark (±4 points), intrinsic property quality and contextual bonuses (view, olive trees, energy class), penalties for constraints and risks. 7-10 is a bargain, 5-7 fair price, below 5 overpriced.",
      },
    ],
  },
  {
    id: "fiscalita",
    title: "Taxes and costs",
    items: [
      {
        q: "How much IMU do you pay on a second home in Apulia?",
        a: "IMU on a second home is calculated on the revalued cadastral income × multiplier (160 for category A residences). The standard rate is 8.6‰ but Municipalities can raise it up to 10.6‰. In Salento rates range from 8.6‰ (small towns) to 10.6‰ (Lecce, Ostuni, Otranto).",
      },
      {
        q: "How much does buying a house cost including taxes?",
        a: "On a primary residence from a private seller: registration tax 2% (on cadastral value), mortgage + cadastral tax €50 + €50, notary €1,500-3,000, real-estate agency 3% + VAT. On a second home: registration tax 9% on cadastral value. Budget +12-15% of the price for a second home.",
      },
      {
        q: "Is it worth using the primary-residence tax break?",
        a: "Yes, if you transfer residence to the Municipality within 18 months and don't own other homes in Italy. You save ~5-7% on the price. Not convenient if you want to rent the property out within the first 5 years (you lose the break) or already have another primary residence.",
      },
    ],
  },
  {
    id: "vincoli",
    title: "Constraints and bureaucracy",
    items: [
      {
        q: "What is the landscape constraint?",
        a: "The landscape constraint (D.lgs 42/2004) requires landscape authorisation for any external change to the property (windows, plaster, extensions). In Salento it is very widespread: it covers the 300m coastal strip, historic centres, monumental olive trees, SIC/ZPS areas. Always check the municipal PUG before buying.",
      },
      {
        q: "Can you build within 300 metres from the sea in Apulia?",
        a: "No, except on pre-existing buildings. The Galasso constraint (L. 431/85) blocks new constructions in the 300m strip from the shoreline. Only routine and extraordinary maintenance on already-authorised buildings is allowed. No volumetric extensions, no raising of floors.",
      },
      {
        q: "Can trulli be turned into B&Bs or hotels?",
        a: "Yes, but under strict constraints. The original cone of the trullo is a historic-architectural heritage asset and cannot be altered. Accessory rooms (kitchen, bathrooms, connecting lamia) can be added only if pre-existing or compliant with the Regional Landscape Plan (PPTR).",
      },
      {
        q: "What are urban-planning and cadastral compliance?",
        a: "Urban-planning compliance: the actual state of the property matches the project authorised by the Municipality. Cadastral compliance: the cadastral floor plan matches reality. They are different: a survey by a qualified technician is required. Non-compliance = unauthorised works, must be regularised before deed (or the contract is null).",
      },
    ],
  },
  {
    id: "tipologie",
    title: "Property types",
    items: [
      {
        q: "What is a Salento pajara?",
        a: "The pajara is a dry-stone rural building typical of southern Salento, similar to the trullo but smaller and built with a different technique (stones without mortar, false-dome roof). It was used by shepherds and farmers as temporary shelter. Today it is valued as a landscape heritage asset.",
      },
      {
        q: "What is the difference between a trullo and a lamia?",
        a: "The trullo has a circular floor plan and a pseudo-dome conical roof, typical of the Valle d'Itria (Alberobello, Cisternino, Locorotondo, Martina, Ostuni). The lamia has a rectangular plan and a flat or barrel roof. Both are dry-stone, originally rural structures.",
      },
      {
        q: "What does 'first-row seafront villa' actually mean?",
        a: 'It means the property has no other buildings between itself and the sea, with a direct view of the shoreline. It is the top of the tourist market but almost always subject to constraints (within the 300m strip, landscape constraint). Beware of listings that claim "sea view" without specifying the actual distance.',
      },
    ],
  },
  {
    id: "investimento",
    title: "Investment and yield",
    items: [
      {
        q: "How much does a short-let yield in Salento?",
        a: "It depends on the area. Ostuni, Otranto and Gallipoli generate 6-10% annual gross yield. Lecce historic centre 5-8%. Brindisi and Taranto cities 4-6%. Subtract 15-25% for management, maintenance and vacancy to estimate the real net yield.",
      },
      {
        q: "Short-let tourist rental or long-term residential — which is better?",
        a: "Short-let: higher yield (6-10%) but strong seasonality, intense management, evolving local rules. Long-term: lower yield (3-5%) but stable cashflow, less management. In pure tourist towns (Gallipoli, Otranto) short-let wins; in university cities (Lecce) long-term is more balanced.",
      },
      {
        q: "Is buying a house in Salento worth it in 2026?",
        a: 'Yes, if you buy below OMI value, avoid unmanageable constraints and have a 5-7+ year horizon. The market has been heavily speculative since 2018, but some areas (Lecce hinterland, Brindisi, Taranto) are still underpriced. Avoid emotional purchases in "trophy" markets (Ostuni centre, Otranto seafront).',
      },
    ],
  },
  {
    id: "rischi",
    title: "Risks and protections",
    items: [
      {
        q: "How do I know if a property listing is overpriced?",
        a: "Compare €/m² with the OMI zone average. If it's more than +20% without a clear reason (direct sea view, complete recent restoration, certified monumental olive trees), it's almost certainly overpriced. Also check time on market: listings older than 6 months without changes are usually mispriced.",
      },
      {
        q: "What is Xylella and how does it affect land purchases?",
        a: "Xylella fastidiosa is a bacterium that has devastated Salento olive groves since 2013. Before buying land with olive trees: request a recent phytosanitary certificate (max 6 months), check the olives are not monumental (if they are and infected, removal is regulated).",
      },
      {
        q: "Can I buy a house at auction in Salento?",
        a: "Yes but with caution. Auctions offer discounts of 30-50% versus the market, but the properties often have issues (occupants, constraints, works to be done). Always review the appraisal, the auction rules, and visit before the auction. Treat it as a speculative investment, not residential.",
      },
    ],
  },
];

export const FAQ_GROUPS_BY_LANG = { it: FAQ_IT, en: FAQ_EN };

/** Return FAQ groups for the given language, gracefully falling back to IT. */
export const getFaqGroups = (lang = "it") =>
  FAQ_GROUPS_BY_LANG[lang] || FAQ_GROUPS_BY_LANG.it;

// Back-compat default export — existing call sites expecting plain IT.
export const FAQ_GROUPS = FAQ_IT;
